"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DimensionLeaderGenerator = void 0;
const three_1 = require("three");
const FontLoader_js_1 = require("three/examples/jsm/loaders/FontLoader.js");
const TextGeometry_js_1 = require("three/examples/jsm/geometries/TextGeometry.js");
const consts_1 = require("../../consts");
class DimensionLeaderGenerator {
    constructor() {
        this.loadDefaultFont();
    }
    generateFromParams({ length, labelText, orientation = 'horizontal' }) {
        const dimensionLeader = new three_1.Group();
        const labelLeftArrow = DimensionLeaderGenerator.generateLabelLeftArrow(length);
        const labelRightArrow = DimensionLeaderGenerator.generateLabelRightArrow(length);
        const label = DimensionLeaderGenerator.generateLabel(length, labelText, this.font);
        const endMarkers = DimensionLeaderGenerator.generateEndMarkers(length, orientation);
        dimensionLeader.add(labelLeftArrow, labelRightArrow, label, endMarkers);
        if (orientation === 'vertical') {
            const quaternion = new three_1.Quaternion();
            quaternion.setFromAxisAngle(consts_1.consts.DIRECTIONS_LIST.Z.clone(), Math.PI / 2);
            dimensionLeader.applyQuaternion(quaternion);
        }
        return dimensionLeader;
    }
    loadDefaultFont() {
        return __awaiter(this, void 0, void 0, function* () {
            const fontLoader = new FontLoader_js_1.FontLoader();
            this.font = yield fontLoader.loadAsync(DimensionLeaderGenerator.FONT_PATHNAME);
        });
    }
    static getArrowInstance(length, direction) {
        const [arrowHeadLength, arrowHeadWidth] = [
            consts_1.consts.SCREENSHOT_DIMENSION_ARROW_HEAD_LENGTH,
            consts_1.consts.SCREENSHOT_DIMENSION_ARROW_HEAD_WIDTH,
        ];
        const arrow = new three_1.ArrowHelper();
        arrow.setLength(length / 2, arrowHeadLength, arrowHeadWidth);
        arrow.setColor(consts_1.consts.SCREENSHOT_DIMENSION_LEADER_COLOR);
        arrow.setDirection(direction);
        return arrow;
    }
    static generateLabelLeftArrow(length) {
        return DimensionLeaderGenerator.getArrowInstance(length, consts_1.consts.DIRECTIONS_LIST['-X'].clone());
    }
    static generateLabelRightArrow(length) {
        return DimensionLeaderGenerator.getArrowInstance(length, consts_1.consts.DIRECTIONS_LIST.X.clone());
    }
    static generateEndMarkers(length, orientation) {
        const points = [];
        // Left end marker
        points.push(new three_1.Vector3(-0.5, 0.5, 0));
        points.push(new three_1.Vector3(-0.5, -0.5, 0));
        // Right end marker
        points.push(new three_1.Vector3(0.5, 0.5, 0));
        points.push(new three_1.Vector3(0.5, -0.5, 0));
        const geometry = new three_1.BufferGeometry().setFromPoints(points);
        const markerHeight = consts_1.consts.SCREENSHOT_DIMENSION_LEADER_END_MARKER_HEIGHT;
        const endMarkerYTranslation = orientation === 'vertical'
            ? markerHeight / -2 // Arrows should touch top of the marker
            : markerHeight / 2; // Arrows should touch bottom of the marker
        const endMarkers = new three_1.LineSegments(geometry, DimensionLeaderGenerator.LINE_MATERIAL.clone());
        endMarkers.position.set(0, endMarkerYTranslation, 0);
        endMarkers.scale.set(length, markerHeight, 1);
        return endMarkers;
    }
    static generateLabel(dimensionLength, text, font) {
        const label = new three_1.Group();
        // Prepare label text
        const labelTextGeometry = new TextGeometry_js_1.TextGeometry(text, {
            font,
            size: consts_1.consts.SCREENSHOT_DIMENSION_LABEL_FONT_SIZE,
            height: 0.001,
            curveSegments: 12,
        });
        labelTextGeometry.computeBoundingBox();
        if (!labelTextGeometry.boundingBox) {
            return label;
        }
        const boundingBoxCenter = labelTextGeometry.boundingBox.getCenter(new three_1.Vector3());
        const labelText = new three_1.Mesh(labelTextGeometry, DimensionLeaderGenerator.LINE_MATERIAL.clone());
        labelText.position.copy(boundingBoxCenter.multiplyScalar(-1));
        label.add(labelText);
        const labelTextSize = labelTextGeometry.boundingBox.getSize(new three_1.Vector3());
        const [labelWidth, labelHeight] = [
            labelTextSize.x + consts_1.consts.SCREENSHOT_DIMENSION_LABEL_PADDING,
            labelTextSize.y + consts_1.consts.SCREENSHOT_DIMENSION_LABEL_PADDING,
        ];
        // Prepare label background
        const labelPlaneGeometry = new three_1.PlaneGeometry(labelWidth, labelHeight);
        const labelPlaneMaterial = new three_1.MeshBasicMaterial({ color: consts_1.consts.SCREENSHOT_DIMENSION_LABEL_BACKGROUND });
        const labelPlane = new three_1.Mesh(labelPlaneGeometry, labelPlaneMaterial);
        label.add(labelPlane);
        // Prepare label border
        const halfLabelWidth = labelWidth / 2;
        const halfLabelHeight = labelHeight / 2;
        const labelBorderPoints = [
            new three_1.Vector3(-halfLabelWidth, halfLabelHeight, 0),
            new three_1.Vector3(-halfLabelWidth, -halfLabelHeight, 0),
            new three_1.Vector3(halfLabelWidth, -halfLabelHeight, 0),
            new three_1.Vector3(halfLabelWidth, halfLabelHeight, 0),
            new three_1.Vector3(-halfLabelWidth, halfLabelHeight, 0),
        ];
        const labelBorderGeometry = new three_1.BufferGeometry().setFromPoints(labelBorderPoints);
        const labelBorder = new three_1.Line(labelBorderGeometry, DimensionLeaderGenerator.LINE_MATERIAL.clone());
        label.add(labelBorder);
        // Dimension is narrow than the label hence shift label to right and add label leader to left of the label
        const maxAcceptableLabelWidth = dimensionLength - 2 * consts_1.consts.SCREENSHOT_DIMENSION_ARROW_HEAD_LENGTH;
        if (labelWidth > maxAcceptableLabelWidth) {
            const extraLabelLeaderPoints = [
                new three_1.Vector3(-consts_1.consts.SCREENSHOT_DIMENSION_EXTRA_NARROW_LABEL_LEADER_WIDTH, 0, 0),
                new three_1.Vector3(0, 0, 0),
            ];
            const extraLabelLeaderGeometry = new three_1.BufferGeometry().setFromPoints(extraLabelLeaderPoints);
            const extraLabelLeader = new three_1.LineSegments(extraLabelLeaderGeometry, DimensionLeaderGenerator.LINE_MATERIAL.clone());
            extraLabelLeader.translateX(-labelWidth / 2);
            label.add(extraLabelLeader);
            const labelTranslationForNarrowDimension = dimensionLength / 2 + consts_1.consts.SCREENSHOT_DIMENSION_EXTRA_NARROW_LABEL_LEADER_WIDTH + labelWidth / 2;
            label.translateX(labelTranslationForNarrowDimension);
        }
        return label;
    }
}
exports.DimensionLeaderGenerator = DimensionLeaderGenerator;
DimensionLeaderGenerator.dimensionLeaderHeight = consts_1.consts.SCREENSHOT_DIMENSION_LEADER_END_MARKER_HEIGHT +
    consts_1.consts.SCREENSHOT_DIMENSION_LABEL_HEIGHT / 2 +
    consts_1.consts.SCREENSHOT_DIMENSION_LABEL_MARGIN_X_2;
DimensionLeaderGenerator.LINE_MATERIAL = new three_1.MeshBasicMaterial({ color: consts_1.consts.SCREENSHOT_DIMENSION_LEADER_COLOR });
DimensionLeaderGenerator.FONT_PATHNAME = '../../../assets/fonts/OpenSans_Regular.typeface.json';
